import { createEffect } from "solid-js";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import type { useTheme } from "@/providers/theme-provider";
import type { NavigateFn } from "@tanstack/solid-router";
import { resetUndoScope, undoLastAction } from "@/lib/undo";
import { useSession } from "@/lib/auth";

interface AppShortcutsOptions {
  navigate: NavigateFn;
  theme: ReturnType<typeof useTheme>;
  setShortcutsOpen: (open: boolean) => void;
  setIsSidebarOpen: (f: (prev: boolean) => boolean) => void;
  openNewHabitDialog: () => void;
}

export function useAppShortcuts(options: AppShortcutsOptions) {
  // ── Shortcut handlers (declarative; bindings live in lib/shortcuts/defaults.ts)
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    options.setShortcutsOpen(true);
  });
  useCommandHandler(COMMAND_IDS.HABIT_NEW, () => {
    options.openNewHabitDialog();
  });

  // ── Undo (L1) ──────────────────────────────────────────────────────────
  // Ctrl/Cmd+Z outside inputs (`when` lives in defaults.ts). Stack is
  // per-account: when active account changes (multi-account/login/logout) it is
  // cleared — an inverse must NEVER be executed against another session.
  const session = useSession();
  createEffect(() => {
    resetUndoScope(session.userId());
  });
  useCommandHandler(COMMAND_IDS.UNDO, () => {
    void undoLastAction();
  });

  // ── UI toggles ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    options.setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void options.theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void options.theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    options.theme.toggleBackgroundStyle();
  });

  // ── Navigation ─────────────────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.GOTO_TODAY, () => {
    void options.navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_HABITS, () => {
    void options.navigate({ to: "/habits" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_CALENDAR, () => {
    void options.navigate({ to: "/calendar" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_STATS, () => {
    void options.navigate({ to: "/stats" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void options.navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void options.navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void options.navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void options.navigate({ to: "/language" });
  });
}
